/**
 * F-126/F-133/F-139 BGM — the doc-shaped half of the coverage rule: read a
 * board's run column and "BGM" column and hand `computeBgmCoverage` the flat
 * rows it wants. Split from `../bgm-coverage.js` so the rule itself stays a
 * pure function over plain data (trivially testable, no Yjs), while this thin
 * adapter owns the one thing that IS doc knowledge: finding the columns.
 *
 * Used by both sides, which is the point — the client to draw the board's
 * coverage panel and name the gaps as the designer fills them, the server to
 * derive `BoardSummary.bgmStems` for every surface that shows the stems tag
 * without opening a doc.
 */
import { computeBgmCoverage } from "../bgm-coverage.js";
import { SHORT_COLUMN_NAME } from "../shorts.js";
import { BGM_COLUMN_NAME, bgmStemsOf, MOOD_COLUMN_NAME, SECTION_COLUMN_NAME } from "./schema.js";
/**
 * First column on `sheetId` named `name` (exact match), or undefined.
 *
 * FIRST, not "the only one": nothing stops a user renaming a second column to
 * "Section", and picking the leftmost gives every client the same answer
 * (`sortedColIds` is the shared fractional order) instead of whichever one a
 * map iteration happened to yield.
 */
export function findColumnByName(board, sheetId, name) {
    for (const colId of board.sortedColIds(sheetId)) {
        if (board.getColumn(sheetId, colId)?.name === name)
            return colId;
    }
    return undefined;
}
/** Locate the run column, the BGM column and the Short column on `sheetId`. */
export function bgmColumns(board, sheetId) {
    const sectionColId = findColumnByName(board, sheetId, SECTION_COLUMN_NAME);
    const moodColId = sectionColId === undefined ? findColumnByName(board, sheetId, MOOD_COLUMN_NAME) : undefined;
    const shortColId = findColumnByName(board, sheetId, SHORT_COLUMN_NAME);
    const runColId = sectionColId ?? moodColId ?? shortColId;
    return {
        runColId,
        runKind: sectionColId !== undefined
            ? "section"
            : moodColId !== undefined
                ? "mood"
                : shortColId !== undefined
                    ? "short"
                    : undefined,
        bgmColId: findColumnByName(board, sheetId, BGM_COLUMN_NAME),
        shortColId,
    };
}
/**
 * Coverage for one sheet. With no run column the script has not been divided
 * yet, so there are zero runs and there is nothing to score — the same answer
 * `computeBgmCoverage([])` gives, reached without walking rows.
 *
 * A MISSING BGM column is not an error either: it reads as "no stem anywhere",
 * which is exactly true before the first one is added.
 */
export function boardBgmCoverage(board, sheetId) {
    const { runColId, bgmColId, shortColId } = bgmColumns(board, sheetId);
    if (runColId === undefined)
        return computeBgmCoverage([]);
    const rows = board.sortedRowIds(sheetId).map((rowId) => {
        const slot = bgmColId === undefined ? undefined : board.getCellBgm(sheetId, rowId, bgmColId);
        const short = shortColId === undefined ? "" : board.getCellText(sheetId, rowId, shortColId).trim();
        // F-141: on a Shorts board a line with a short but no mood yet still has
        // music to be found for it — it runs under its short's name until the
        // mood is written; a line with neither joins no run, as always.
        const label = runColId === shortColId
            ? short === ""
                ? ""
                : `Short ${short}`
            : board.getCellText(sheetId, rowId, runColId).trim() || (short === "" ? "" : `Short ${short}`);
        return {
            rowId,
            label,
            stems: slot === undefined ? 0 : bgmStemsOf(slot).length,
            ...(short !== "" ? { group: short } : {}),
        };
    });
    return computeBgmCoverage(rows);
}
/**
 * Coverage across EVERY sheet, folded into one verdict — what the board's
 * stems tag reports, since the tag is about the BOARD.
 *
 * In practice this is the single-sheet answer: F-21 was dropped and the UI
 * creates exactly one grid per board (the `sheets` map only stays in the
 * schema for compatibility). It is written this way anyway because this is
 * the number the dashboard shows: if a second sheet ever appears, an unscored
 * one must keep the tag ongoing rather than slip through a hard-coded
 * "first sheet".
 */
export function boardBgmCoverageAllSheets(board) {
    const sheets = board.listSheets();
    const runs = sheets.flatMap((sheet) => boardBgmCoverage(board, sheet.id).runs);
    const covered = runs.filter((run) => run.covered).length;
    return { runs, covered, total: runs.length, ready: runs.length > 0 && covered === runs.length };
}
//# sourceMappingURL=bgm.js.map